import type { UUID } from '@/types/Id'

// --- Types matching backend slim models ---

export interface SharePointCountResponse {
  count: number
  has_more: boolean
}

export interface SharePointSite {
  id: string
  name?: string
  createdDateTime: string // ISO string
  webUrl: string
  count?: SharePointCountResponse
}

export interface SharePointLibrary {
  id: string
  name: string
  createdDateTime: string // ISO string
  lastModifiedDateTime: string // ISO string
  webUrl: string
  description?: string
  createdBy?: unknown // Could be refined if needed
  count?: SharePointCountResponse
  siteId?: string
}

// --- Query key helpers ---

interface SharePointSitesQueryKeyArgs {
  fetchAll?: boolean
  orgId: UUID | ''
  rootsOnly?: boolean
}

interface SharePointPaginatedSitesQueryKeyArgs {
  limit: number
  orgId: UUID | ''
  rootsOnly?: boolean
}

interface SharePointLibrariesQueryKeyArgs {
  orgId: UUID | ''
  siteId: string
}

interface SharePointLibrariesSelectedQueryKeyArgs {
  orgId: UUID | ''
}

interface SharePointAccessTokenQueryKeyArgs {
  orgId: UUID | ''
}

export const sharepointSitesQueryKeys = {
  all: () => ['sharepoint_sites'] as const,
  organization: (keyArgs: SharePointSitesQueryKeyArgs) =>
    [
      'sharepoint_sites',
      keyArgs.orgId,
      keyArgs.rootsOnly ? 'roots_only' : 'all_sites',
      keyArgs.fetchAll ? 'fetch_all' : 'single_page',
    ] as const,
  paginated: (keyArgs: SharePointPaginatedSitesQueryKeyArgs) =>
    [
      'sharepoint_sites',
      'paginated',
      keyArgs.orgId,
      keyArgs.rootsOnly ? 'roots_only' : 'all_sites',
      keyArgs.limit,
    ] as const,
}

interface SharePointSubsitesQueryKeyArgs {
  orgId: UUID | ''
  siteId: string
}

interface SharePointPaginatedSubsitesQueryKeyArgs {
  limit: number
  orgId: UUID | ''
  siteId: string
}

export const sharepointSubsitesQueryKeys = {
  all: () => ['sharepoint_subsites'] as const,
  site: (keyArgs: SharePointSubsitesQueryKeyArgs) =>
    ['sharepoint_subsites', keyArgs.orgId, keyArgs.siteId] as const,
  paginatedSite: (keyArgs: SharePointPaginatedSubsitesQueryKeyArgs) =>
    [
      'sharepoint_subsites',
      'paginated',
      keyArgs.orgId,
      keyArgs.siteId,
      keyArgs.limit,
    ] as const,
}

export const sharepointAccessTokenQueryKeys = {
  all: () => ['sharepoint_access_token'] as const,
  organization: (keyArgs: SharePointAccessTokenQueryKeyArgs) =>
    ['sharepoint_access_token', keyArgs.orgId] as const,
}

export const sharepointLibrariesQueryKeys = {
  all: () => ['sharepoint_libraries'] as const,
  site: (keyArgs: SharePointLibrariesQueryKeyArgs) =>
    ['sharepoint_libraries', keyArgs.orgId, keyArgs.siteId] as const,
  selected: (keyArgs: SharePointLibrariesSelectedQueryKeyArgs) =>
    ['sharepoint_libraries', 'selected', keyArgs.orgId] as const,
}

interface SharePointListsQueryKeyArgs {
  orgId: UUID | ''
}

export const sharepointListsQueryKeys = {
  all: () => ['sharepoint_lists'] as const,
  organization: (keyArgs: SharePointListsQueryKeyArgs) =>
    ['sharepoint_lists', keyArgs.orgId] as const,
}

export const getSharepointLibraryKey = (
  siteId: string | undefined,
  libraryId: string,
) => {
  if (!siteId) {
    return libraryId
  }
  return `${siteId}-${libraryId}`
}

// --- File Browser Types ---

export interface SharePointDrive {
  id: string
  name: string
  description?: string | null
  web_url: string
  drive_type: string
}

export interface SharePointDriveListResponse {
  count: number
  data: SharePointDrive[]
  access_limited?: boolean
}

export interface SharePointResolveUrlRequest {
  url: string
}

export interface SharePointResolvedLocationResponse {
  site: SharePointSite
  drive?: SharePointDrive | null
}

export interface SharePointDriveItem {
  id: string
  name: string
  size?: number | null
  is_folder: boolean
  web_url?: string | null
  last_modified?: string | null // ISO string
  mime_type?: string | null
  parent_path?: string | null
  drive_id?: string | null
  child_count?: number | null
}

export interface SharePointChildrenResponse {
  items: SharePointDriveItem[]
  next_cursor?: string | null
  has_more: boolean
}

export interface SharePointSyncedFilesCountResponse {
  count: number
  limit: number
}

export interface SharePointBatchImportItem {
  drive_id: string
  item_id: string
  is_folder: boolean
}

export interface SharePointBatchImportRequest {
  folder_id?: UUID | null
  items: SharePointBatchImportItem[]
  preserve_folder_structure?: boolean
  tag_ids?: UUID[]
}

export interface SharePointBatchImportResponse {
  task_id: string
  message: string
}

export type SharePointCloud = 'global' | 'gcc' | 'gcc_high'

// --- File Browser Query Keys ---

interface SharePointDrivesQueryKeyArgs {
  orgId: UUID | ''
  siteId: string
}

interface SharePointChildrenQueryKeyArgs {
  orgId: UUID | ''
  driveId: string
  itemId?: string // undefined for root
  cursor?: string | null
}

interface SharePointSyncedFilesCountQueryKeyArgs {
  orgId: UUID | ''
}

export const sharepointDrivesQueryKeys = {
  all: () => ['sharepoint_drives'] as const,
  site: (keyArgs: SharePointDrivesQueryKeyArgs) =>
    ['sharepoint_drives', keyArgs.orgId, keyArgs.siteId] as const,
}

export const sharepointChildrenQueryKeys = {
  all: () => ['sharepoint_children'] as const,
  folder: (keyArgs: SharePointChildrenQueryKeyArgs) =>
    [
      'sharepoint_children',
      keyArgs.orgId,
      keyArgs.driveId,
      keyArgs.itemId || 'root',
      keyArgs.cursor || '',
    ] as const,
}

export const sharepointSyncedFilesCountQueryKeys = {
  all: () => ['sharepoint_synced_files_count'] as const,
  organization: (keyArgs: SharePointSyncedFilesCountQueryKeyArgs) =>
    ['sharepoint_synced_files_count', keyArgs.orgId] as const,
}

interface SharePointBatchImportStatusQueryKeyArgs {
  orgId: UUID | ''
}

export const sharepointBatchImportStatusQueryKeys = {
  all: () => ['sharepoint_batch_import_status'] as const,
  organization: (keyArgs: SharePointBatchImportStatusQueryKeyArgs) =>
    ['sharepoint_batch_import_status', keyArgs.orgId] as const,
}

interface SharePointBatchImportMutationKeyArgs {
  orgId: UUID | ''
}

export const sharepointMutationKeys = {
  batchImportStart: (keyArgs: SharePointBatchImportMutationKeyArgs) =>
    ['sharepoint_batch_import_start', keyArgs.orgId] as const,
}
